import { useTranslation } from 'react-i18next';
import { useForm, FormProvider } from 'react-hook-form';
import * as yup from 'yup';
import { useQueryClient } from 'react-query';

import { Box, Button, Divider, MenuItem, useTheme } from '@mui/material';
import { yupResolver } from '@hookform/resolvers/yup';

import { Currency } from '@bandido/shared/constants';

import { ControlledTextField } from '@frontend/features/common/components/ControlledTextField';
import { ControlledSelect } from '@frontend/features/common/components/ControlledSelect';
import { countries } from '@frontend/features/common/constants/countries';
import { CountryFlag } from '@frontend/features/common/components/CountryFlag';
import { client } from '@frontend/features/common/services';
import { QueryKeys } from '@frontend/features/common/types';
import { useSnackBar } from '@frontend/features/common/components/SnackBarContext';
import { ControlledCheckbox } from '@frontend/features/common/components/ControlledCheckbox';

interface FormProps {
  onClose: () => void;
}

interface CreateAgencytDTO {
  name: string;
  country: string;
  currency: string;
  isPrivate?: boolean;
}

export const Form = ({ onClose }: FormProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { addToast } = useSnackBar();
  const queryClient = useQueryClient();

  const resolver = yupResolver(
    yup.object().shape({
      name: yup.string().required(),
      country: yup
        .string()
        .test((value) => value !== 'no-selection')
        .required(),
      currency: yup
        .string()
        .test((value) => value !== 'no-selection')
        .required(),
      isPrivate: yup.boolean().optional(),
    }),
  );

  const methods = useForm({
    resolver,
  });
  const { handleSubmit, control } = methods;

  const onSubmit = async (data: CreateAgencytDTO) => {
    try {
      await client.post('/agency', data);
      addToast({
        message: t('agencies.create.createdSuccess'),
        color: 'success',
      });
    } catch (e) {
      addToast({
        message: t('agencies.create.createdError'),
        color: 'error',
      });
      console.log(e);
    } finally {
      queryClient.invalidateQueries({ queryKey: QueryKeys.getAllAgencies });
      onClose();
    }
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box
          display="grid"
          gridTemplateColumns="repeat(12, 1fr)"
          gap={2}
          sx={{
            maxHeight: 'calc(100vh - 300px)',
            overflowX: 'auto',
            paddingTop: theme.spacing(1),
          }}
        >
          <Box gridColumn="span 12">
            <ControlledTextField
              control={control}
              name="name"
              label={t('agencies.create.name')}
              placeholder={t('common.form.onlyLetters')}
              required
              errorMessage={t('common.form.required')}
              fullWidth
            />
          </Box>
          <Box gridColumn="span 12">
            <ControlledSelect
              variant="outlined"
              name="country"
              required
              control={control}
              placeholder={t('common.form.select')}
              label={t('agencies.create.country')}
              size="small"
              errorMessage={t('common.form.required')}
              fullWidth
            >
              {countries.map((country) => (
                <MenuItem key={country.code} value={country.name}>
                  <CountryFlag countryCode={country.code} />
                  {country.name}
                </MenuItem>
              ))}
            </ControlledSelect>
          </Box>

          <Box gridColumn="span 12">
            <ControlledSelect
              variant="outlined"
              name="currency"
              required
              control={control}
              placeholder={t('common.form.select')}
              label={t('agencies.create.currency')}
              size="small"
              errorMessage={t('common.form.required')}
              fullWidth
            >
              {/* TODO: vamos a precisar un endpoint de currencies */}
              <MenuItem value={Currency.USD}>{Currency.USD}</MenuItem>
              <MenuItem value={Currency.UYU}>{Currency.UYU}</MenuItem>
              <MenuItem value={Currency.PYG}>{Currency.PYG}</MenuItem>
            </ControlledSelect>
          </Box>

          <Box gridColumn="span 12">
            <ControlledCheckbox name="isPrivate" control={control} label={t('agencies.create.isPrivate')} />
          </Box>
        </Box>
        <Box display="flex" flexDirection="column" gap={theme.spacing(3)} paddingTop={theme.spacing(3)}>
          <Divider />
          <Box
            display="flex"
            justifyContent="space-evenly"
            width="100%"
            gap={theme.spacing(1)}
            sx={{
              '& > button': {
                width: 'calc(50% - 4px)',
              },
            }}
          >
            <Button variant="outlined" color="error" size="medium" onClick={onClose}>
              {t('common.form.discard')}
            </Button>
            <Button variant="contained" color="primary" type="submit" size="medium">
              {t('common.form.save')}
            </Button>
          </Box>
        </Box>
      </form>
    </FormProvider>
  );
};
